import { useEffect, useRef, useState, type ReactNode, type UIEvent } from "react";
import type { CodeEditorProps } from "./public";
import styles from "./CodeEditor.module.css";

type MonacoEditorModule = typeof import("./MonacoCodeEditor");

let loadedMonacoModule: MonacoEditorModule | null = null;
let monacoModulePromise: Promise<MonacoEditorModule | null> | null = null;

// Monaco 청크는 한 번만 받는다. 받지 못하면 null 로 고정해 textarea 폴백을 남긴다. 사내망이나 CSP 가
// 청크를 막아도 사람은 본문을 읽고 고칠 수 있어야 한다.
function loadMonacoModule(): Promise<MonacoEditorModule | null> {
  if (loadedMonacoModule !== null) {
    return Promise.resolve(loadedMonacoModule);
  }
  monacoModulePromise ??= import("./MonacoCodeEditor")
    .then((module) => {
      loadedMonacoModule = module;
      return module;
    })
    .catch(() => null);
  return monacoModulePromise;
}

function useMonacoModule(): MonacoEditorModule | null {
  const [module, setModule] = useState<MonacoEditorModule | null>(loadedMonacoModule);
  useEffect(() => {
    if (module !== null) {
      return;
    }
    let active = true;
    void loadMonacoModule().then((loaded) => {
      if (active && loaded !== null) {
        setModule(loaded);
      }
    });
    return () => {
      active = false;
    };
  }, [module]);
  return module;
}

/**
 * 코드 편집기. hatchery-registry-frontend 의 `CodeEditor` 를 옮겼다(뒤에 c6 로 옮길 예정이다).
 *
 * Monaco 는 npm 번들의 lazy 청크로 받는다. CDN 은 쓰지 않는다. 청크를 받는 동안과 받지 못했을 때는
 * textarea 로 대신한다. 부모는 높이가 있는 flex column 이어야 한다. 편집기는 남은 높이를 채운다.
 */
export function CodeEditor(props: CodeEditorProps): ReactNode {
  const monacoModule = useMonacoModule();
  // 사람이 폴백 textarea 에 입력하는 중이면 blur 까지 Monaco 로 바꾸지 않는다. 포커스 중에 DOM 을 바꾸면
  // 새 Monaco 가 커서를 본문 끝에 두어 입력 위치가 튄다.
  const [fallbackFocused, setFallbackFocused] = useState(false);
  if (monacoModule === null || fallbackFocused) {
    return (
      <TextareaCodeEditor
        {...props}
        onFocus={() => setFallbackFocused(true)}
        onBlur={() => setFallbackFocused(false)}
      />
    );
  }
  const MonacoCodeEditor = monacoModule.default;
  return <MonacoCodeEditor {...props} />;
}

function TextareaCodeEditor({
  ariaLabel,
  onBlur,
  onChange,
  onFocus,
  value,
}: CodeEditorProps & { onFocus: () => void; onBlur: () => void }): ReactNode {
  const gutterRef = useRef<HTMLPreElement>(null);
  const syncGutterScroll = (event: UIEvent<HTMLTextAreaElement>) => {
    if (gutterRef.current) {
      gutterRef.current.scrollTop = event.currentTarget.scrollTop;
    }
  };
  return (
    <div className={styles.fallbackShell}>
      <pre ref={gutterRef} className={styles.fallbackLineNumbers} aria-hidden="true">
        {buildLineNumbers(value)}
      </pre>
      <textarea
        aria-label={ariaLabel}
        className={styles.editor}
        spellCheck={false}
        value={value}
        onBlur={onBlur}
        onChange={(event) => onChange(event.currentTarget.value)}
        onFocus={onFocus}
        onScroll={syncGutterScroll}
      />
    </div>
  );
}

function buildLineNumbers(value: string): string {
  const lineCount = Math.max(1, value.split("\n").length);
  return Array.from({ length: lineCount }, (_, index) => String(index + 1)).join("\n");
}
